import React, { useEffect, useState, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import Header from '../Header';
import ChatMessage from '../ChatMessage';
import InputArea from '../InputArea';
import { fetchCandidateData } from '../../api/chat';
import { 
  ChatContainerWrapper, 
  ChatMessages, 
  LoadingContainer, 
  StartButton,
  ShareButton,
  PresentationMessage,
  InitialContainer
} from './styles.js';
import { ClipLoader } from 'react-spinners';
import useConversation from './components/conversation';

const coach = {
  name: 'Rê',
  avatar: '/images/coach-profile.png',
};

const ChatContainer = ({ interviews, onNewInterview }) => {
  const location = useLocation();
  const { talent_uuid } = useParams();
  const [candidate, setCandidate] = useState(null);
  const [talent, setTalent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [interviewStarted, setInterviewStarted] = useState(false);
  const [vacancyTitle, setVacancyTitle] = useState('');
  const [companyName, setCompanyName] = useState('');
  const searchParams = new URLSearchParams(location.search);
  const app_id = searchParams.get('app_id');
  const token = searchParams.get('token');
  const chatMessagesEndRef = useRef(null);

  const {
    conversation,
    handleSendMessage,
    inputDisabled,
    finalMessage,
    loadingInterview,
    interview,
    setInterview,
    interviewFinished, // Receber o novo estado
    loadingFeedback, // Receber o novo estado
    interviewStored, // Receber o novo estado
  } = useConversation(talent, talent_uuid, app_id, candidate, vacancyTitle, companyName, interviewStarted);

  useEffect(() => {
    const getCandidateData = async () => {
      try {
        const data = await fetchCandidateData(talent_uuid, app_id, token);
        if (!data || data.status === 404) {
          setCandidate(null);
        } else {
          setCandidate(data.user);
          setTalent(data);
          setVacancyTitle(data.application.vacancy.title);
          setCompanyName(data.application.vacancy.company.name);
          setInterview(data.interview);
          onNewInterview(data.application.vacancy.title, data.application.vacancy.company.name, talent_uuid, app_id);
        }
      } catch (error) {
        console.error('Error fetching candidate data:', error);
        setCandidate(null);
      } finally {
        setLoading(false);
      }
    };

    getCandidateData();
  }, [talent_uuid, app_id, onNewInterview]);

  useEffect(() => {
    if (chatMessagesEndRef.current) {
      chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversation, loadingInterview]);

  const startInterview = () => {
    setInterviewStarted(true);
  };

  const interviewId = interviewStored ? interviewStored?.interview?._id : interview ? interview._id : null;

  return (
    <ChatContainerWrapper>
      <Header 
        vacancyTitle={vacancyTitle} 
        companyName={companyName}
      />
      {loading ? (
        <LoadingContainer>
          <ClipLoader color="#00BFFF" loading={loading} size={150} />
        </LoadingContainer>
      ) : candidate === null ? (
        <LoadingContainer>
          <h2 style={{ color: 'white' }}>Não foi possível obter a entrevista</h2>
        </LoadingContainer>
      ) : (
        <>
          {loadingInterview ? (
            <LoadingContainer>
              <ClipLoader color="#00BFFF" loading={loadingInterview} size={150} />
            </LoadingContainer>
          ) : (
            <>
              {interviewStarted ? (
                <>
                  <ChatMessages>
                    {conversation.map((message, index) => (
                      <ChatMessage
                        key={index}
                        type={message.type}
                        text={message.text}
                        audioUrl={message.audioUrl}
                        user={message.user}
                        avatar={message.type === "coach" ? '/images/coach-profile.png' : talent.photo_url}
                      />
                    ))}
                    {finalMessage && loadingFeedback && (
                      <ChatMessage
                        type="coach"
                        text="Bom, vamos lá ao seu feedback..."
                        user={coach}
                        avatar={'/images/coach-profile.png'}
                        withSpinner
                      />
                    )}
                    <div ref={chatMessagesEndRef} />
                  </ChatMessages>
                  {interviewFinished ? (
                    <div style={{ textAlign: '-webkit-center' }}>
                      <a 
                        target='_blank' 
                        style={{ textDecoration: 'none' }}
                        href={`https://training.recrutei.com.br/public/interview/${interviewId}?talent=${talent_uuid}`}
                      >
                        <ShareButton>Compartilhe essa Entrevista (Link Público)</ShareButton>
                      </a>
                      <br></br>
                      <a 
                        target='_blank' 
                        style={{ textDecoration: 'none' }}
                        href='https://empregos.recrutei.com.br?utm_campaign=interview-finish'
                      >
                        <StartButton>Conheça a Recrutei Empregos</StartButton>
                      </a>
                    </div>
                  ) : (
                    <InputArea onSendMessage={handleSendMessage} inputDisabled={inputDisabled} />
                  )}
                </>
              ) : (
                <LoadingContainer>
                  <InitialContainer>
                    <PresentationMessage>
                      <img src="/images/icons/interview.png" height={100} alt="Interview Icon"></img>
                      <h3>
                        <b>{candidate?.name.split(' ')[0]}</b>
                      </h3>
                      Seja bem vindo(a) ao ambiente de treinamento para entrevistas da Recrutei. Essa é uma oportunidade
                      para que você teste e aprimore seus conhecimentos, a partir de um cenário fictício de entrevista de emprego,
                      com base nos seus dados (currículo, experiências, formações, etc) e nos dados da vaga em que está se aplicando.
                      Vale ressaltar, que este é um ambiente de testes, que utiliza a Rê (a inteligência artificial da Recrutei), e 
                      nenhuma das perguntas realizadas foi previamente escolhida ou determinada pela empresa contratante.
                      <div>
                        <br></br>
                        <b>Dica: </b>Envie mensagens com áudio, a entrevista fica mais real! 😉
                      </div>
                    </PresentationMessage>
                    {interview ? <><small style={{ marginBottom: '15px' }}>Essa entrevista já foi finalizada.</small></> : ''}
                    <StartButton onClick={startInterview}>{interview ? "Visualizar Entrevista" : "Iniciar Entrevista"}</StartButton>
                    <br></br>
                    <small>Obs.: Essa é uma versão BETA. Erros poderão acontecer!</small>
                  </InitialContainer>
                </LoadingContainer>
              )}
            </>
          )}
        </>
      )}
    </ChatContainerWrapper>
  );
};

export default ChatContainer;
